import {gpsControlAllows,validGpsSetting} from '../shared/gps-control-profile.js';
import {reportedGpsSetting} from '../shared/gps-setting-verification.js';
import {LocalApiError,type GpsSettingAction,type GpsSettingHistory,type GpsIntervalVerification} from './local-api.ts';

type Saved={status:string;text:string;verification?:GpsIntervalVerification|null};
type State={value:string;loading:boolean;saving:boolean;history:GpsSettingHistory|null;error:string|null;retryAfterSeconds:number;cooldownUntil:number};
type Options={profile:unknown;action:GpsSettingAction;canSave:()=>boolean;load:()=>Promise<GpsSettingHistory>;save:(value:number)=>Promise<Saved>};

// Each instance belongs to one account/device/profile/setting. The view remounts
// it when that identity changes; disposal makes all outstanding replies inert.
export class GpsSettingEditor{
 private options:Options;private active=false;private revision=0;private edited=false;
 private state:State={value:'',loading:false,saving:false,history:null,error:null,retryAfterSeconds:0,cooldownUntil:0};
 private listeners=new Set<()=>void>();
 constructor(options:Options){this.options=options;}
 getSnapshot=()=>this.state;
 subscribe=(listener:()=>void)=>{this.listeners.add(listener);return()=>{this.listeners.delete(listener);};};
 private publish(patch:Partial<State>){this.state={...this.state,...patch};for(const listener of this.listeners)listener();}
 private current(revision:number){return this.active&&revision===this.revision;}
 start(){this.active=true;void this.load();return()=>{this.active=false;this.revision++;};}
 setValue=(value:string)=>{if(!this.active||this.state.saving)return;this.edited=true;this.publish({value,error:null});};
 private applyHistory(history:GpsSettingHistory){
  const read=reportedGpsSetting(this.options.profile,this.options.action,history.lastSuccessful?.reply);
  // Unknown settings remain blank. A previous desired value is not a reading.
  this.publish({history,...(!this.edited&&read!==null?{value:String(read)}:{})});
 }
 load=async()=>{
  if(!this.active||this.state.saving)return;
  const revision=++this.revision;this.publish({loading:true,error:null});
  try{const history=await this.options.load();if(this.current(revision))this.applyHistory(history);}
  catch{if(this.current(revision))this.publish({error:'history_unavailable'});}
  finally{if(this.current(revision))this.publish({loading:false});}
 };
 save=async()=>{
  const {profile,action}=this.options;
  if(!this.active||this.state.saving||!this.options.canSave()||Date.now()<this.state.cooldownUntil||!gpsControlAllows(profile,action)||!/^\d+$/.test(this.state.value)||!validGpsSetting(Number(this.state.value),action))return false;
  const revision=++this.revision,value=Number(this.state.value),requestedAt=new Date().toISOString();
  this.edited=true;this.publish({saving:true,loading:false,error:null,cooldownUntil:Date.now()+10000});
  try{
   const result=await this.options.save(value);if(!this.current(revision))return false;
   this.publish({history:{lastRequest:{value,status:result.status,reply:result.text,verification:result.verification,requestedAt,finishedAt:new Date().toISOString()},lastSuccessful:this.state.history?.lastSuccessful??null}});
   return result.verification?.status==='confirmed';
  }catch(error){
   if(this.current(revision)){
    const code=error instanceof LocalApiError?error.code:'unknown',seconds=error instanceof LocalApiError?error.retryAfterSeconds??60:60;
    this.publish({error:code,retryAfterSeconds:seconds,...(code==='rateLimited'?{cooldownUntil:Date.now()+seconds*1000}:{})});
   }return false;
  }finally{
   // GET reads receipts only; it never retries the hardware write or query.
   if(this.current(revision))try{const history=await this.options.load();if(this.current(revision))this.applyHistory(history);}catch{}
   if(this.current(revision))this.publish({saving:false});
  }
 };
}
